import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  HeadstoneDetailsData,
  headstoneDetailsService,
} from '../../../../services/headstoneDetailsInfo';
import { tasksService } from '../../../../services/tasks';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('HeadstoneDetailsData');

type RouteParams = {
  headstoneDetailsId?: string;
  targetUserId?: string;
  viewOnly?: boolean;
};

type HeadstoneDetailsDataScreenRouteProp = RouteProp<RootStackParamList, 'HeadstoneDetailsData'>;

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const HeadstoneDetailsDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<HeadstoneDetailsDataScreenRouteProp>();
  const [headstoneDetailsInfo, setHeadstoneDetailsInfo] = useState<HeadstoneDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const headstoneDetailsId = route.params?.headstoneDetailsId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();

  useEffect(() => {
    loadHeadstoneDetailsInfo();
  }, []);

  const loadHeadstoneDetailsInfo = async () => {
    try {
      let info = null;
      if (headstoneDetailsId) {
        info = await headstoneDetailsService.getHeadstoneDetailsById(headstoneDetailsId);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await headstoneDetailsService.getAllHeadstoneDetails();
        if (allInfo.length > 0) {
          info = allInfo[0];
        }
      }

      if (info) {
        // Skip loading portal credentials in viewOnly mode - they are protected until EOL
        if (!viewOnly || isDeathVerifiedForSelectedUser) {
          try {
            logger.debug('[Portal Debug] Attempting to load portal credentials for headstone details ID:', info.id);
            const portalData = await vaultService.getPortalCredentials(info.id, 'headstone-details');
            logger.debug('[Portal Debug] Portal data retrieved for headstone details:', info.id, portalData);
            if (portalData) {
              info.username = portalData.username;
              info.password = portalData.password;
              info.accountNumber = portalData.accountNumber;
              logger.debug('[Portal Debug] Assigned credentials to headstone details data');
            } else {
              logger.debug('[Portal Debug] No portal data returned for headstone details:', info.id);
            }
          } catch (vaultError) {
            logger.debug('[Portal Debug] Error loading portal data for headstone details:', info.id, vaultError);
          }
        }

        setHeadstoneDetailsInfo(info);
      }
    } catch (error) {
      logger.error('Error loading headstone details info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (headstoneDetailsInfo) {
      navigation.navigate('HeadstoneDetailsInput', {
        headstoneDetailsId: headstoneDetailsInfo.id,
        isEdit: true
      });
    } else {
      navigation.navigate('HeadstoneDetailsInput');
    }
  };

  const handleDelete = () => {
    if (headstoneDetailsInfo) {
      Alert.alert(
        'Delete Headstone Details',
        'Are you sure you want to delete this headstone details? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                // Delete the record
                await headstoneDetailsService.deleteHeadstoneDetails(headstoneDetailsInfo.id!);

                // CRITICAL: Check if this was the last entry and reset task if needed
                const remainingData = await headstoneDetailsService.getAllHeadstoneDetails();
                if (!remainingData || remainingData.length === 0) {
                  // Reset task completion status if no data remains
                  await tasksService.resetTask('headstone_details');
                }

                // Navigate back to checklist
                navigation.navigate('FinalWishesChecklist');
              } catch (error) {
                logger.error('Error deleting headstone details:', error);
                Alert.alert('Error', 'Failed to delete headstone details');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('FinalWishesChecklist');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Headstone Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Headstone Details</Text>
      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {viewOnly && (
            <View style={styles.viewOnlyBanner}>
              <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
            </View>
          )}
          {headstoneDetailsInfo ? (
            <>
              {headstoneDetailsInfo.headstonePreference && (
                <InfoItem label="Headstone Preference" value={headstoneDetailsInfo.headstonePreference} />
              )}
              {headstoneDetailsInfo.materialPreference && (
                <InfoItem label="Material Preference" value={headstoneDetailsInfo.materialPreference} />
              )}
              {headstoneDetailsInfo.sizePreference && (
                <InfoItem label="Size Preference" value={headstoneDetailsInfo.sizePreference} />
              )}
              {headstoneDetailsInfo.stylePreference && (
                <InfoItem label="Style Preference" value={headstoneDetailsInfo.stylePreference} />
              )}
              {headstoneDetailsInfo.colorPreference && (
                <InfoItem label="Color Preference" value={headstoneDetailsInfo.colorPreference} />
              )}
              {headstoneDetailsInfo.inscriptionText && (
                <InfoItem label="Inscription Text" value={headstoneDetailsInfo.inscriptionText} />
              )}
              {headstoneDetailsInfo.epitaph && (
                <InfoItem label="Epitaph" value={headstoneDetailsInfo.epitaph} />
              )}
              {headstoneDetailsInfo.symbolsImagery && (
                <InfoItem label="Symbols/Imagery" value={headstoneDetailsInfo.symbolsImagery} />
              )}
              {headstoneDetailsInfo.fontStyle && (
                <InfoItem label="Font Style" value={headstoneDetailsInfo.fontStyle} />
              )}
              {headstoneDetailsInfo.datesFormat && (
                <InfoItem label="Dates Format" value={headstoneDetailsInfo.datesFormat} />
              )}
              {headstoneDetailsInfo.additionalEngravings && (
                <InfoItem label="Additional Engravings" value={headstoneDetailsInfo.additionalEngravings} />
              )}
              {headstoneDetailsInfo.monumentCompany && (
                <InfoItem label="Monument Company" value={headstoneDetailsInfo.monumentCompany} />
              )}
              {headstoneDetailsInfo.companyContact && (
                <InfoItem label="Company Contact" value={headstoneDetailsInfo.companyContact} />
              )}
              {headstoneDetailsInfo.budgetRange && (
                <InfoItem label="Budget Range" value={headstoneDetailsInfo.budgetRange} />
              )}
              {headstoneDetailsInfo.notes && (
                <InfoItem label="Notes" value={headstoneDetailsInfo.notes} />
              )}

              {/* Vault Credentials Section */}
              {(headstoneDetailsInfo.username || headstoneDetailsInfo.password || headstoneDetailsInfo.accountNumber) && (
                <>
                  <View style={styles.vaultSection}>
                    <Text style={styles.vaultSectionTitle}>🔒 Monument Company Portal Credentials</Text>
                    <Text style={styles.vaultSubtext}>Securely stored in your vault</Text>
                  </View>

                  {headstoneDetailsInfo.accountNumber && (
                    <InfoItem label="Account Number" value={headstoneDetailsInfo.accountNumber} />
                  )}

                  {headstoneDetailsInfo.username && (
                    <View style={styles.infoItem}>
                      <Text style={styles.label}>Username</Text>
                      <View style={styles.credentialRow}>
                        <Text style={styles.value}>
                          {showUsername ? headstoneDetailsInfo.username : '••••••••'}
                        </Text>
                        <TouchableOpacity
                          style={styles.eyeButton}
                          onPress={() => setShowUsername(!showUsername)}
                        >
                          <Image
                            source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                            style={styles.eyeIcon}
                          />
                        </TouchableOpacity>
                      </View>
                      <View style={styles.separator} />
                    </View>
                  )}

                  {headstoneDetailsInfo.password && (
                    <View style={styles.infoItem}>
                      <Text style={styles.label}>Password</Text>
                      <View style={styles.credentialRow}>
                        <Text style={styles.value}>
                          {showPassword ? headstoneDetailsInfo.password : '••••••••'}
                        </Text>
                        <TouchableOpacity
                          style={styles.eyeButton}
                          onPress={() => setShowPassword(!showPassword)}
                        >
                          <Image
                            source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                            style={styles.eyeIcon}
                          />
                        </TouchableOpacity>
                      </View>
                      <View style={styles.separator} />
                    </View>
                  )}
                </>
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No headstone details found.</Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Headstone Details</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  vaultSection: {
    marginVertical: ms(20),
    paddingTop: ms(10),
    borderTopWidth: ms(2),
    borderTopColor: '#E5E7EB',
  },
  vaultSectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(5),
  },
  vaultSubtext: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginBottom: ms(15),
  },
  credentialRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default HeadstoneDetailsDataScreen;
